import { call, put, select } from 'redux-saga/effects';

import { goToSiteService, goToFactoryService, goToRootService } from './router';
import { initializeCoreService } from './core';
import { logoutService } from './login';
import { closeModalService } from './modal';
import { deleteNotificationsRequest, fetchUsersRequest } from '../requests';

import {
  currentModalSelector,
  currentUserIdSelector,
  currentUserSelector,
  isAttachmentWithIdExistsSelector,
  pathSelector,
} from '../../../selectors';
import {
  createActionReceived,
  createAttachmentReceived,
  createSiteReceived,
  createDeviceLabelReceived,
  createDeviceMembershipReceived,
  createDeviceReceived,
  createLabelReceived,
  createLineReceived,
  createNotificationReceived,
  createFactoryMembershipReceived,
  createFactoryReceived,
  createAlarmReceived,
  createUserReceived,
  createModelReceived,
  createProtocolReceived,
  createAlertSettingReceived,
  deleteActionReceived,
  deleteAttachmentReceived,
  deleteDeviceLabelReceived,
  deleteDeviceMembershipReceived,
  deleteDeviceReceived,
  deleteSiteReceived,
  deleteLabelReceived,
  deleteLineReceived,
  deleteNotificationReceived,
  deleteFactoryMembershipReceived,
  deleteFactoryReceived,
  deleteAlarmReceived,
  deleteUserReceived,
  deleteModelReceived,
  deleteProtocolReceived,
  deleteAlertSettingReceived,
  socketDisconnected,
  socketReconnected,
  updateActionReceived,
  updateAttachmentReceived,
  updateSiteReceived,
  updateDeviceReceived,
  updateLabelReceived,
  updateLineReceived,
  updateFactoryReceived,
  updateAlarmReceived,
  updateUserReceived,
  updateModelReceived,
  updateProtocolReceived,
  updateAlertSettingReceived,
  updateAlertsReceived,
  updateItemsDeviceReceived,
  hideDeviceReceived,
  createChannelReceived,
  updateChannelReceived,
  deleteChannelReceived,
  createItemReceived,
  updateItemReceived,
  deleteItemReceived,
} from '../../../actions';
import ModalTypes from '../../../constants/ModalTypes';

export function* socketDisconnectedService() {
  yield put(socketDisconnected());
}

export function* socketReconnectedService() {
  yield put(socketReconnected());
  yield call(initializeCoreService);
}

export function* createUserReceivedService(user) {
  yield put(createUserReceived(user));
}

export function* updateUserReceivedService(user) {
  const currentUser = yield select(currentUserSelector);

  if (user.id === currentUser.id) {
    if (currentUser.isAdmin) {
      if (!user.isAdmin) {
        const currentModal = yield select(currentModalSelector);

        if (currentModal === ModalTypes.USERS) {
          yield call(closeModalService);
        }
      }
    } else if (user.isAdmin) {
      yield call(fetchUsersRequest);
    }
  }

  yield put(updateUserReceived(user));
}

export function* deleteUserReceivedService(user) {
  const currentUserId = yield select(currentUserIdSelector);

  if (user.id === currentUserId) {
    yield call(logoutService);
  }

  yield put(deleteUserReceived(user));
}

export function* createFactoryReceivedService(factory, factoryMembership) {
  yield put(createFactoryReceived(factory, factoryMembership));
}

export function* createModelReceivedService(model) {
  yield put(createModelReceived(model));
}

export function* createProtocolReceivedService(protocol) {
  yield put(createProtocolReceived(protocol));
}

export function* updateFactoryReceivedService(factory) {
  yield put(updateFactoryReceived(factory));
}

export function* deleteFactoryReceivedService(factory) {
  const { factoryId } = yield select(pathSelector);

  if (factory.id === factoryId) {
    yield call(goToRootService);
  }

  yield put(deleteFactoryReceived(factory));
}

export function* createFactoryMembershipReceivedService(factoryMembership, user) {
  yield put(createFactoryMembershipReceived(factoryMembership, user));
}

export function* deleteFactoryMembershipReceivedService(factoryMembership) {
  yield put(deleteFactoryMembershipReceived(factoryMembership));
}

export function* createSiteReceivedService(site, lines, labels) {
  yield put(createSiteReceived(site, lines, labels));
}

export function* updateSiteReceivedService(site) {
  yield put(updateSiteReceived(site));
}

export function* deleteSiteReceivedService(site) {
  const { siteId, factoryId } = yield select(pathSelector);

  if (site.id === siteId) {
    yield call(goToFactoryService, factoryId);
  }

  yield put(deleteSiteReceived(site));
}

export function* createLineReceivedService(line) {
  yield put(createLineReceived(line));
}

export function* updateLineReceivedService(line) {
  yield put(updateLineReceived(line));
}

export function* deleteLineReceivedService(line) {
  yield put(deleteLineReceived(line));
}

export function* createLabelReceivedService(label) {
  yield put(createLabelReceived(label));
}

export function* updateLabelReceivedService(label) {
  yield put(updateLabelReceived(label));
}

export function* deleteLabelReceivedService(label) {
  yield put(deleteLabelReceived(label));
}

export function* deleteModelReceivedService(model) {
  yield put(deleteModelReceived(model));
}

export function* deleteProtocolReceivedService(protocol) {
  yield put(deleteProtocolReceived(protocol));
}

export function* createDeviceReceivedService(
  device,
  deviceMemberships,
  deviceLabels,
  alarms,
  attachments,
) {
  yield put(createDeviceReceived(device, deviceMemberships, deviceLabels, alarms, attachments));
}

export function* updateDeviceReceivedService(device) {
  yield put(updateDeviceReceived(device));
}

export function* hideDeviceReceivedService(device) {
  yield put(hideDeviceReceived(device));
}

export function* deleteDeviceReceivedService(device) {
  const { deviceId, siteId } = yield select(pathSelector);

  if (device.id === deviceId) {
    yield call(goToSiteService, siteId);
  }

  yield put(deleteDeviceReceived(device));
}

export function* createDeviceMembershipReceivedService(deviceMembership) {
  yield put(createDeviceMembershipReceived(deviceMembership));
}

export function* deleteDeviceMembershipReceivedService(deviceMembership) {
  yield put(deleteDeviceMembershipReceived(deviceMembership));
}

export function* createDeviceLabelReceivedService(deviceLabel) {
  yield put(createDeviceLabelReceived(deviceLabel));
}

export function* deleteDeviceLabelReceivedService(deviceLabel) {
  yield put(deleteDeviceLabelReceived(deviceLabel));
}

export function* createAlarmReceivedService(alarm) {
  yield put(createAlarmReceived(alarm));
}

export function* updateAlarmReceivedService(alarm) {
  yield put(updateAlarmReceived(alarm));
}

export function* deleteAlarmReceivedService(alarm) {
  yield put(deleteAlarmReceived(alarm));
}

export function* createAttachmentReceivedService(attachment, requestId) {
  const isExists = yield select(isAttachmentWithIdExistsSelector, requestId);

  if (!isExists) {
    yield put(createAttachmentReceived(attachment));
  }
}

export function* updateAttachmentReceivedService(attachment) {
  yield put(updateAttachmentReceived(attachment));
}

export function* deleteAttachmentReceivedService(attachment) {
  yield put(deleteAttachmentReceived(attachment));
}

export function* createActionReceivedService(action) {
  yield put(createActionReceived(action));
}

export function* updateActionReceivedService(action) {
  yield put(updateActionReceived(action));
}

export function* deleteActionReceivedService(action) {
  yield put(deleteActionReceived(action));
}

export function* createNotificationReceivedService(notification, user, device, action) {
  const { deviceId } = yield select(pathSelector);

  if (device.id === deviceId) {
    yield call(deleteNotificationsRequest, [notification.id]);
  } else {
    yield put(createNotificationReceived(notification, user, device, action));
  }
}

export function* deleteNotificationReceivedService(notification) {
  yield put(deleteNotificationReceived(notification));
}

export function* updateModelReceivedService(model) {
  yield put(updateModelReceived(model));
}

export function* updateProtocolReceivedService(protocol) {
  yield put(updateProtocolReceived(protocol));
}

export function* createAlertSettingReceivedService(alertSetting) {
  yield put(createAlertSettingReceived(alertSetting));
}

export function* updateAlertSettingReceivedService(alertSetting) {
  yield put(updateAlertSettingReceived(alertSetting));
}

export function* deleteAlertSettingReceivedService(alertSetting) {
  yield put(deleteAlertSettingReceived(alertSetting));
}

export function* updateAlertsReceivedService(alerts) {
  yield put(updateAlertsReceived(alerts));
}

export function* updateItemsDeviceReceivedService(items) {
  yield put(updateItemsDeviceReceived(items));
}

export function* createChannelReceivedService(channel) {
  yield put(createChannelReceived(channel));
}

export function* updateChannelReceivedService(channel) {
  yield put(updateChannelReceived(channel));
}

export function* deleteChannelReceivedService(channel) {
  yield put(deleteChannelReceived(channel));
}

export function* createItemReceivedService(item) {
  yield put(createItemReceived(item));
}

export function* updateItemReceivedService(item) {
  yield put(updateItemReceived(item));
}

export function* deleteItemReceivedService(item) {
  yield put(deleteItemReceived(item));
}
